import { ApiError, apiErrorCode } from "@/lib/api";
import {
  hasCapability,
  type AuthedRequester,
  type BrokerIdentity,
} from "@/lib/brokerAuth";
import { messageFor } from "@/lib/errors";
import { formatShortDate } from "@/lib/listFormat";
import {
  readActionStanding,
  type ActionStanding,
  type SignalRequestRefusal,
} from "@/lib/submissions/actionItems";
import { KEY_REUSED_MESSAGE } from "@/lib/submissions/quoteBinding";

// BRK-S395 — a carrier Signal quotes by hand (PMS-S819/S820; Vesper, Nav
// 10/22). It has no API, so the portal never asks it questions or for a
// quote. After Submit, the broker asks Signal from its card: PMS opens a
// `quote_request` action, Tyler or Ryan key the application into the
// carrier's website and enter the quote, which then shows on the card like
// any other and binds through Signal.

/** The longest note PMS takes with a manual quote request. */
export const MANUAL_NOTE_MAX = 2000;

/** Whether the broker may ask Signal for a manual carrier's quote: none
 *  asked yet, or the last one closed by Signal, for a broker with
 *  `submission:edit`. */
export function canRequestManualQuote(
  request: ActionStanding | null,
  identity: BrokerIdentity | null,
): boolean {
  return (
    (!request || request.status === "cancelled") &&
    hasCapability(identity, "submission:edit")
  );
}

/** Where a manual carrier with no quote yet stands, in a line. */
export function manualQuoteLine(
  carrier: string,
  request: ActionStanding | null,
): string {
  return request && request.status !== "cancelled"
    ? `Requested ${formatShortDate(request.requestedAt)}. Signal will add the quote here.`
    : `Signal gets ${carrier}’s quote from its website. Request a quote, and Signal adds it here.`;
}

/** POST …/carriers/{carrierId}/quote-request with the attempt's
 *  `Idempotency-Key`: the same key answers the item it opened, so a retry
 *  never asks twice. A blank note is left out. */
export async function requestManualQuote(
  request: AuthedRequester,
  submissionId: string,
  carrierId: string,
  input: { idempotencyKey: string; note?: string },
): Promise<ActionStanding> {
  const note = input.note?.trim();
  const answer = await request<unknown>(
    `/api/broker/submissions/${encodeURIComponent(submissionId)}/carriers/${encodeURIComponent(carrierId)}/quote-request`,
    {
      method: "POST",
      body: note ? { note } : {},
      headers: { "Idempotency-Key": input.idempotencyKey },
    },
  );
  const standing = readActionStanding(answer);
  if (!standing) {
    throw new ApiError(
      200,
      "Signal answered, but not with the request. Reload to check.",
    );
  }
  return standing;
}

/** A refused manual quote request, in words to act on. */
export function manualRefusalOf(err: unknown): SignalRequestRefusal {
  if (err instanceof ApiError && err.status === 0) {
    return {
      kind: "network",
      message:
        "We couldn’t confirm your request. Check the carrier’s card before trying again.",
    };
  }
  switch (apiErrorCode(err)) {
    case "quote_request_in_progress":
      return {
        kind: "settled",
        message: "A quote request for this carrier is already with Signal.",
      };
    case "not_manual_carrier":
      return {
        kind: "settled",
        message: "This carrier quotes online, so it doesn’t need a request.",
      };
    case "not_submitted":
      return {
        kind: "settled",
        message: "Get Quotes on the application first, then request the quote.",
      };
    // PMS's sentence is about our key, not the broker's request.
    case "idempotency_key_reused":
      return { kind: "key_reused", message: KEY_REUSED_MESSAGE };
    default:
      return { kind: "refused", message: messageFor(err) };
  }
}
